import { useEffect, useState } from 'react';
import { Card, StatusBadge, Modal } from '@/components/ui';
import { adminApi, type CrmIntegrationStatus } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { fmtDateTime } from './common';

/**
 * כרטיס "CRM (Kliento)" — מצב החיבור של העסק ל-CRM. החיבור עצמו נעשה מצד
 * ה-CRM (מסך "פלאגין מרכזיה" שם, עם משתמש בעלים/מנהל של GoCall); כאן רואים
 * מצב, מתי סונכרן לאחרונה, שגיאה אחרונה, ואפשר לנתק.
 */
export function CrmKlientoCard() {
  const [status, setStatus] = useState<CrmIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      setStatus(await adminApi.crmIntegration());
    } catch {
      toast('שגיאה בטעינת מצב ה-CRM', 'danger');
    } finally {
      setLoading(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      setStatus(await adminApi.disconnectCrmIntegration());
      toast('החיבור ל-CRM נותק', 'ok');
      setConfirm(false);
    } catch {
      toast('שגיאה בניתוק', 'danger');
    } finally {
      setBusy(false);
    }
  }

  const connected = !!status?.connected;
  const d = status?.deliveries_24h;

  return (
    <Card title="CRM (Kliento)">
      {loading ? (
        <span style={{ color: 'var(--muted)' }}>טוען…</span>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <strong>{status?.crm_name || 'CRM'}</strong>
            <StatusBadge tone={connected ? 'ok' : 'neutral'} dot size="sm">
              {connected ? 'מחובר' : status?.status === 'disconnected' ? 'מנותק' : 'לא מחובר'}
            </StatusBadge>
          </div>

          {connected ? (
            <>
              <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 'var(--t-sm)' }}>
                <dt style={{ color: 'var(--muted)' }}>חובר ע״י</dt>
                <dd style={{ margin: 0 }}>{status?.connected_by?.name ?? '—'}{status?.connected_by?.email ? ` · ${status.connected_by.email}` : ''}</dd>
                <dt style={{ color: 'var(--muted)' }}>מועד חיבור</dt>
                <dd style={{ margin: 0 }}>{fmtDateTime(status?.connected_at)}</dd>
                <dt style={{ color: 'var(--muted)' }}>אירוע אחרון</dt>
                <dd style={{ margin: 0 }}>{fmtDateTime(status?.last_event_at)}</dd>
                <dt style={{ color: 'var(--muted)' }}>משלוחים 24ש׳</dt>
                <dd style={{ margin: 0 }}>
                  {d ? `${d.success} הצליחו · ${d.failed + d.pending} ממתינים/ניסיון חוזר · ${d.dead} נכשלו` : '—'}
                </dd>
                {status?.last_error && (
                  <>
                    <dt style={{ color: 'var(--danger, var(--muted))' }}>שגיאה אחרונה</dt>
                    <dd dir="ltr" style={{ margin: 0, color: 'var(--danger, inherit)', overflowWrap: 'anywhere', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}>{status.last_error}</dd>
                  </>
                )}
              </dl>
              <button type="button" className="btn danger" onClick={() => setConfirm(true)} disabled={busy} style={{ alignSelf: 'flex-start' }}>
                נתק
              </button>
            </>
          ) : (
            <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
              החיבור נעשה מתוך ה-CRM: הגדרות → פלאגין מרכזיה → חיבור, עם אימייל וסיסמה של משתמש בעלים/מנהל בעסק זה.
              {status?.disconnected_at ? ` נותק ב-${fmtDateTime(status.disconnected_at)}; חיבור מחדש שומר את הקישורים הקיימים.` : ''}
            </p>
          )}
        </div>
      )}

      {confirm && (
        <Modal
          isOpen
          onClose={() => setConfirm(false)}
          title="ניתוק CRM (Kliento)"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(false)} disabled={busy}>ביטול</button>
              <button type="button" className="btn danger" onClick={() => void disconnect()} disabled={busy}>{busy ? 'מנתק…' : 'נתק'}</button>
            </>
          }
        >
          <p>לנתק את ה-CRM? הסנכרון ייפסק ומפתח הגישה של ה-CRM יבוטל. הקישורים בין לידים לרשומות ב-CRM נשמרים, וחיבור מחדש מה-CRM יחזיר את הסנכרון.</p>
        </Modal>
      )}
    </Card>
  );
}
